import React from "react";
import { AlertTriangle } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import {
  getModalStyles,
  getPrimaryText,
  getSecondaryText,
  getLightBorder,
} from "../../utils/themeUtils";
import { Button } from "../ui";
import type { LeadConvertPreview } from "../../api/leadsAPI";
import { convertBillingLines } from "../crm/ConvertLeadDialog";

/**
 * "This family is still a lead" prompt, shown before staff create an invoice
 * or take a payment on a lead's account.
 *
 * Dan, 2026-10-09: "there should be something that prompts conversion if
 * invoice creation is attempted for a lead - don't block." So this never
 * blocks: staff can keep the family as a lead and carry on, convert them
 * first and carry on, or go back to what they were typing.
 *
 * Same family of dialog as LeadEnrollConfirmModal (the warning before a lead
 * is ENROLLED), and driven by one hook: hooks/useLeadBillingPrompt. What
 * converting would bill is the server's own answer (the Convert dialog's
 * preview), never a guess made here.
 */
export type LeadBillingAction = "invoice" | "payment" | "invoice_and_payment" | "sale" | "registration";

export interface LeadBillingPromptModalProps {
  isOpen: boolean;
  /** Family name for the sentence, e.g. "The Rivera family". Optional. */
  familyName?: string | null;
  /** What staff were about to do, for the sentence. */
  action: LeadBillingAction;
  /** What converting would bill. null while loading or when it could not be read. */
  preview: LeadConvertPreview | null;
  previewState: "loading" | "ready" | "error";
  onGoBack: () => void;
  onKeepAsLead: () => void;
  onConvertFirst: () => void;
}

const ACTION_WORDS: Record<LeadBillingAction, string> = {
  invoice: "create an invoice on their account",
  payment: "take a payment on their account",
  invoice_and_payment: "create an invoice and take a payment on their account",
  sale: "put a sale on their account",
  registration: "register them, which can add a charge to their account",
};

/** True when converting would bill nothing at all right now. */
export function convertingBillsNothing(preview: LeadConvertPreview | null): boolean {
  return !!preview && !preview.registration_fee.will_bill && !preview.will_enroll;
}

const LeadBillingPromptModal: React.FC<LeadBillingPromptModalProps> = ({
  isOpen,
  familyName,
  action,
  preview,
  previewState,
  onGoBack,
  onKeepAsLead,
  onConvertFirst,
}) => {
  const { isDarkMode } = useTheme();

  if (!isOpen) return null;

  const who = familyName && familyName.trim() ? familyName.trim() : "This family";

  return (
    // z-[60]: opens on top of the dialog or page (z-50) that asked for it.
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-[60] p-4">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="lead-billing-prompt-title"
        aria-describedby="lead-billing-prompt-body"
        className={`${getModalStyles(isDarkMode)} rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto border-2 ${
          isDarkMode ? "border-amber-700/60" : "border-amber-300"
        }`}
      >
        <div className={`flex items-start gap-3 p-5 border-b ${getLightBorder(isDarkMode)}`}>
          <div
            className={`flex-shrink-0 rounded-full p-2 ${
              isDarkMode ? "bg-amber-900/40" : "bg-amber-100"
            }`}
          >
            <AlertTriangle
              className={isDarkMode ? "text-amber-300" : "text-amber-600"}
              size={22}
              aria-hidden="true"
            />
          </div>
          <h3
            id="lead-billing-prompt-title"
            className={`text-lg font-semibold ${getPrimaryText(isDarkMode)}`}
          >
            This family is still a lead
          </h3>
        </div>

        <div id="lead-billing-prompt-body" className={`p-5 space-y-3 text-sm ${getSecondaryText(isDarkMode)}`}>
          <p>
            {who} is a lead, not a member yet, and you are about to {ACTION_WORDS[action]}.
            Nothing has been saved. Would you like to make them a member first?
          </p>
          {/* 2026-10-09: this said "Nothing is billed until an athlete is
              enrolled" while staff were creating an invoice. A lead only
              holds off AUTOMATIC billing; what staff add by hand is billed. */}
          <p data-testid="lead-billing-manual-note">
            Either way, what you add by hand is billed when you save it. Keeping them a lead only
            holds off automatic billing (monthly dues and the registration fee).
          </p>

          <div data-testid="lead-billing-convert-preview">
            {previewState === "loading" && <p>Checking what converting would bill…</p>}
            {previewState === "error" && (
              <p role="alert" className={isDarkMode ? "text-amber-300" : "text-amber-700"}>
                Could not check what converting would bill. If you choose to convert, the next
                step shows it and lets you convert without billing.
              </p>
            )}
            {previewState === "ready" && preview && (
              <>
                {convertingBillsNothing(preview) && (
                  <p className={`font-medium ${getPrimaryText(isDarkMode)}`}>
                    Converting bills nothing right now. Monthly dues start once an athlete is enrolled.
                  </p>
                )}
                <p className={`font-medium mt-1 ${getPrimaryText(isDarkMode)}`}>
                  Converting to a member will:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Make them an active member and mark the lead as won.</li>
                  {convertBillingLines(preview, true).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </>
            )}
          </div>

          <p>
            Either way, what you typed stays as it is and you carry on from where you were.
            Your choice is saved in the activity log.
          </p>
        </div>

        <div className={`flex flex-col sm:flex-row sm:flex-wrap sm:justify-end gap-3 p-4 border-t ${getLightBorder(isDarkMode)}`}>
          <Button type="button" variant="ghost" size="md" onClick={onGoBack}>
            Go back
          </Button>
          <Button type="button" variant="secondary" size="md" onClick={onKeepAsLead}>
            Keep as a lead and continue
          </Button>
          <Button type="button" variant="primary" size="md" onClick={onConvertFirst}>
            Convert to member, then continue
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LeadBillingPromptModal;
